import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import Modal from './Modal'
import contentService from '../services/contentService'
import {
  STATUS_LABELS,
  getStatusLabel,
  diasVencido,
  cierraVencido,
  fetchVencidos,
  sortVencidos,
} from '../utils/vencidos'
import './VencidosModal.css'

const PAGE_SIZE = 25
const hoyStr = () => new Date().toLocaleDateString('en-CA') // YYYY-MM-DD en hora local

const snoozeKey = (user) => `geves_vencidos_modal_${user?.id ?? user?.username ?? 'anon'}`
const getSnooze = (user) => { try { return localStorage.getItem(snoozeKey(user)) } catch { return null } }
const setSnooze = (user) => { try { localStorage.setItem(snoozeKey(user), hoyStr()) } catch { /* sin storage: se mostrará de nuevo */ } }

const cuentaDe = (p) => p.influencer_name || p.influencer || '—'
const formatFecha = (d) =>
  new Date(d.slice(0, 10) + 'T00:00:00').toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })

/**
 * Modal de posts vencidos para el Calendario.
 *  - Paso 1 (alert): resumen con el total y botón "Ver detalle".
 *  - Paso 2 (detail): listado con cambio de estado directo, filtros y acciones masivas.
 * Se muestra como máximo una vez al día por usuario y navegador.
 * Usa la misma definición de "vencido" que /vencidos (utils/vencidos.js).
 */
export default function VencidosModal({ user, enabled = true, onChanged, onDone }) {
  const [step, setStep] = useState('closed') // 'closed' | 'alert' | 'detail'
  const [posts, setPosts] = useState([])
  const [q, setQ] = useState('')
  const [cuenta, setCuenta] = useState('')
  const [estado, setEstado] = useState('')
  const [visible, setVisible] = useState(PAGE_SIZE)
  const [selected, setSelected] = useState(new Set())
  const [saving, setSaving] = useState(new Set())
  const [toast, setToast] = useState(null)
  const changedRef = useRef(false)
  const startedRef = useRef(false)
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone // "terminó" = no hay nada que mostrar o ya se cerró (permite encadenar otros avisos)

  // ── Carga inicial (una sola vez): solo si no se descartó hoy ─────────────
  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true
    if (!enabled || getSnooze(user) === hoyStr()) {
      if (onDoneRef.current) onDoneRef.current()
      return
    }
    fetchVencidos()
      .then(list => {
        if (list.length === 0) { if (onDoneRef.current) onDoneRef.current(); return }
        setPosts(list)
        setStep('alert')
      })
      .catch(() => { if (onDoneRef.current) onDoneRef.current() }) // silencioso: /vencidos sigue disponible
  }, [enabled, user])

  const close = useCallback(() => {
    setSnooze(user)
    setStep('closed')
    setToast(null)
    if (changedRef.current) {
      changedRef.current = false
      if (onChanged) onChanged()
    }
    if (onDoneRef.current) onDoneRef.current()
  }, [user, onChanged])

  useEffect(() => {
    if (step === 'closed') return
    const onKey = (e) => { if (e.key === 'Escape') close() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [step, close])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), toast.undo ? 8000 : 5000)
    return () => clearTimeout(t)
  }, [toast])

  // ── Derivados ────────────────────────────────────────────────────────────
  const breakdown = useMemo(() => {
    const m = {}
    posts.forEach(p => { m[p.status] = (m[p.status] || 0) + 1 })
    return Object.entries(m).sort((a, b) => b[1] - a[1])
  }, [posts])

  const cuentas = useMemo(
    () => [...new Set(posts.map(cuentaDe))].sort((a, b) => a.localeCompare(b, 'es')),
    [posts]
  )

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase()
    return posts.filter(p =>
      (!cuenta || cuentaDe(p) === cuenta) &&
      (!estado || p.status === estado) &&
      (!t || (p.title || '').toLowerCase().includes(t))
    )
  }, [posts, q, cuenta, estado])

  useEffect(() => { setVisible(PAGE_SIZE) }, [q, cuenta, estado])

  const shown = filtered.slice(0, visible)
  const allShownSelected = shown.length > 0 && shown.every(p => selected.has(p.id))

  // ── Acciones ─────────────────────────────────────────────────────────────
  const changeStatus = async (ids, status) => {
    const targets = posts.filter(p => ids.includes(p.id) && p.status !== status)
    if (targets.length === 0) return
    const targetIds = targets.map(p => p.id)
    setSaving(prev => new Set([...prev, ...targetIds]))

    const results = await Promise.allSettled(
      targets.map(p => contentService.updatePost(p.id, { status }))
    )
    const ok = targets.filter((_, i) => results[i].status === 'fulfilled')
    const failed = targets.length - ok.length

    if (ok.length > 0) {
      changedRef.current = true
      const okIds = new Set(ok.map(p => p.id))
      setPosts(curr => cierraVencido(status)
        ? curr.filter(p => !okIds.has(p.id))
        : curr.map(p => (okIds.has(p.id) ? { ...p, status } : p)))
      setSelected(prev => { const n = new Set(prev); okIds.forEach(id => n.delete(id)); return n })
    }

    if (failed === 0) {
      const label = getStatusLabel(status)
      setToast({
        type: 'success',
        message: ok.length === 1
          ? `"${ok[0].title || 'Post'}" marcado como ${label}`
          : `${ok.length} posts marcados como ${label}`,
        undo: ok, // posts originales (con su estado anterior)
      })
    } else {
      setToast({ type: 'error', message: `${ok.length} actualizados, ${failed} con error` })
    }
    setSaving(prev => { const n = new Set(prev); targetIds.forEach(id => n.delete(id)); return n })
  }

  const undo = async () => {
    const originals = toast?.undo
    if (!originals) return
    setToast(null)
    const results = await Promise.allSettled(
      originals.map(p => contentService.updatePost(p.id, { status: p.status }))
    )
    const restored = originals.filter((_, i) => results[i].status === 'fulfilled')
    if (restored.length > 0) {
      setPosts(curr => {
        const map = new Map(curr.map(p => [p.id, p]))
        restored.forEach(p => map.set(p.id, p))
        return sortVencidos([...map.values()])
      })
    }
    if (restored.length < originals.length) {
      setToast({ type: 'error', message: 'No se pudieron deshacer todos los cambios' })
    }
  }

  const toggleOne = (id) => setSelected(prev => {
    const n = new Set(prev)
    n.has(id) ? n.delete(id) : n.add(id)
    return n
  })
  const toggleAllShown = () => setSelected(prev => {
    const n = new Set(prev)
    if (allShownSelected) shown.forEach(p => n.delete(p.id))
    else shown.forEach(p => n.add(p.id))
    return n
  })

  if (step === 'closed') return null

  const total = posts.length
  const plural = total === 1 ? '' : 's'

  return (
    <>
      {/* ── Modal 1: aviso ── */}
      <Modal
        isOpen={step === 'alert'}
        onClose={close}
        minimal
        size="sm"
        title={`Tienes ${total} post${plural} vencido${plural}`}
        footer={(
          <>
            <button className="g-btn g-btn-secondary" onClick={close}>Ahora no</button>
            <button className="g-btn g-btn-primary" onClick={() => setStep('detail')}>Ver detalle</button>
          </>
        )}
      >
        <p className="vm-lead">
          Existe{total === 1 ? '' : 'n'} <strong>{total} post{plural}</strong> que según su fecha de
          publicación ya debería{total === 1 ? '' : 'n'} estar con estado <strong>Publicado</strong>.
        </p>
        <div className="vm-breakdown">
          <span className="vm-breakdown-title">Estado actual de los vencidos</span>
          <div className="vm-chips">
            {breakdown.map(([status, n]) => (
              <span key={status} className="vm-chip">
                {getStatusLabel(status)} <strong>{n}</strong>
              </span>
            ))}
          </div>
        </div>
      </Modal>

      {/* ── Modal 2: detalle ── */}
      <Modal
        isOpen={step === 'detail'}
        onClose={close}
        minimal
        size="xl"
        title={`Posts vencidos · ${total}`}
        subtitle="Cambia el estado aquí mismo. Al elegir Publicado o No Publicado, el post sale de la lista."
        footerAlignment="between"
        footer={(
          <>
            <span className="vm-foot-info">
              Mostrando {shown.length} de {filtered.length}
              {filtered.length !== total ? ` (${total} en total)` : ''} · del más antiguo al más reciente
            </span>
            {/* Estilo minimal: el primer botón queda "ghost" y el último lima (principal). */}
            <div className="vm-foot-actions">
              <button className="g-btn g-btn-secondary" onClick={close}>Cerrar</button>
              <span className="vm-foot-info">
                {selected.size} seleccionado{selected.size === 1 ? '' : 's'}
              </span>
              <button
                className="g-btn g-btn-secondary"
                disabled={selected.size === 0 || saving.size > 0}
                onClick={() => changeStatus([...selected], 'no_publicado')}
              >
                No publicados
              </button>
              <button
                className="g-btn g-btn-primary"
                disabled={selected.size === 0 || saving.size > 0}
                onClick={() => changeStatus([...selected], 'published')}
              >
                Marcar como publicados
              </button>
            </div>
          </>
        )}
      >
        <div className="vm-toolbar">
          <input
            className="vm-input"
            type="search"
            placeholder="Buscar post…"
            aria-label="Buscar post"
            value={q}
            onChange={e => setQ(e.target.value)}
          />
          <select className="vm-select" aria-label="Filtrar por cuenta" value={cuenta} onChange={e => setCuenta(e.target.value)}>
            <option value="">Todas las cuentas</option>
            {cuentas.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="vm-select" aria-label="Filtrar por estado" value={estado} onChange={e => setEstado(e.target.value)}>
            <option value="">Todos los estados</option>
            {breakdown.map(([status]) => <option key={status} value={status}>{getStatusLabel(status)}</option>)}
          </select>
        </div>

        {filtered.length === 0 ? (
          <div className="g-empty">
            <i className="bi bi-check2-circle g-empty-icon" />
            <p className="g-empty-title">{total === 0 ? 'No hay posts vencidos' : 'Ningún post coincide con los filtros'}</p>
            {total === 0 && <p className="g-empty-desc">Todos los posts programados hasta hoy están al día.</p>}
          </div>
        ) : (
          <div className="vm-scroll">
            <table className="g-table vm-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input type="checkbox" aria-label="Seleccionar los visibles" checked={allShownSelected} onChange={toggleAllShown} />
                  </th>
                  <th>Cuenta</th>
                  <th>Post</th>
                  <th>Plataforma</th>
                  <th style={{ minWidth: 210 }}>Estado</th>
                  <th>Fecha programada</th>
                  <th>Días vencido</th>
                </tr>
              </thead>
              <tbody>
                {shown.map(p => {
                  const dias = diasVencido(p.publication_date)
                  const busy = saving.has(p.id)
                  return (
                    <tr key={p.id}>
                      <td>
                        <input type="checkbox" aria-label="Seleccionar post" checked={selected.has(p.id)} onChange={() => toggleOne(p.id)} />
                      </td>
                      <td>{cuentaDe(p)}</td>
                      <td className="vm-title" title={p.title || ''}>{p.title || '(sin título)'}</td>
                      <td>
                        {p.platform_icon_url
                          ? <img src={p.platform_icon_url} alt={p.platform_name || ''} title={p.platform_name || ''} style={{ width: 18, height: 18 }} />
                          : (p.platform_name || '—')}
                      </td>
                      <td>
                        <select
                          className="vm-select vm-select-estado"
                          aria-label={`Estado de ${p.title || 'post'}`}
                          value={p.status}
                          disabled={busy}
                          onChange={e => changeStatus([p.id], e.target.value)}
                        >
                          <optgroup label="Cerrar post">
                            <option value="published">Publicado</option>
                            <option value="no_publicado">No Publicado</option>
                          </optgroup>
                          <optgroup label="Otros estados">
                            {Object.entries(STATUS_LABELS)
                              .filter(([k]) => !cierraVencido(k))
                              .map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                          </optgroup>
                          {!STATUS_LABELS[p.status] && <option value={p.status}>{p.status}</option>}
                        </select>
                      </td>
                      <td>{formatFecha(p.publication_date)}</td>
                      <td>
                        <span className={`g-badge ${dias >= 60 ? 'g-badge-danger' : 'g-badge-warning'}`}>
                          {dias} día{dias === 1 ? '' : 's'}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {filtered.length > visible && (
              <div className="vm-more">
                <button className="g-btn g-btn-secondary" onClick={() => setVisible(v => v + PAGE_SIZE)}>
                  Cargar {Math.min(PAGE_SIZE, filtered.length - visible)} más
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {toast && (
        <div className={`vm-toast vm-toast-${toast.type}`} role="status">
          <span>{toast.message}</span>
          {toast.undo && <button className="vm-toast-btn" onClick={undo}>Deshacer</button>}
          <button className="vm-toast-btn vm-toast-x" aria-label="Cerrar aviso" onClick={() => setToast(null)}>
            <i className="bi bi-x" />
          </button>
        </div>
      )}
    </>
  )
}
